import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { tint } from '../../components/ui/PanelControls';
import { isAudioKind } from './AddAssetPopover';
import { isVideoUrl, getEmbedUrl } from './assetMediaUtils';

/**
 * AssetPreviewModal — full-size preview for one asset: image shown at real
 * size, audio a real `<audio controls>` player (not the cramped 24px-tall
 * inline player this card used to jam in), video with real `<video
 * controls>`. Opened by clicking an asset's own image/media area in
 * AssetLibraryContent.jsx (never the entity-tag notes, never the delete/
 * star/confirm buttons -- those keep their own click handling untouched).
 *
 * Deliberately NOT built on Modal.jsx: that component is a big multi-type
 * FORM modal (a `type` prop picks one of a fixed set of field layouts, and
 * always wraps its content in a submit `<form>`) with no "just show this
 * media, no fields, no submit" mode -- the wrong shape here, same reasoning
 * BoardFilePickerModal.jsx (features/storyboard/) already documents for its
 * own non-form modal. This follows Modal.jsx's own visual conventions
 * (dimmed overlay, card colors/radius/shadow, ESC + overlay-click to close)
 * instead of literally extending it.
 */
const AssetPreviewModal = ({ asset, isDarkMode, onClose }) => {
  const c = tint(isDarkMode);

  useEffect(() => {
    if (!asset) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [asset, onClose]);

  if (!asset) return null;

  const isAudio = isAudioKind(asset.kind);
  // A pasted YouTube/Vimeo link (Asset Library's "Link" mode) has no file
  // extension and needs an <iframe> embed to actually stream -- checked
  // before isVideoUrl, which only recognizes direct file URLs.
  const embedUrl = !isAudio ? getEmbedUrl(asset.imageUrl) : null;
  const isVideo = !isAudio && !embedUrl && isVideoUrl(asset.imageUrl);

  return (
    <div
      onClick={onClose}
      style={{
        display: 'flex', position: 'fixed', inset: 0, zIndex: 1000,
        background: isDarkMode ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)',
        alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', flexDirection: 'column', gap: '10px',
          background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: '10px',
          padding: '14px', maxWidth: 'min(92vw, 900px)', maxHeight: 'calc(100vh - 40px)',
          boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.2)',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {asset.label}
            </div>
            <div style={{ fontSize: '10px', color: c.secondary }}>{asset.kind}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '26px', height: '26px', flexShrink: 0, border: 'none', borderRadius: '5px',
              background: 'transparent', color: c.secondary, cursor: 'pointer',
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {isAudio ? (
            <audio controls src={asset.imageUrl} style={{ width: '100%', minWidth: '320px' }} />
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              title={asset.label}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: 'min(92vw, 860px)', height: 'min(60vh, 484px)', border: 'none', borderRadius: '6px' }}
            />
          ) : isVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- user-supplied reference clip, no caption track exists to offer
            <video controls autoPlay src={asset.imageUrl} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', borderRadius: '6px' }} />
          ) : (
            <img src={asset.imageUrl} alt={asset.label} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', borderRadius: '6px' }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetPreviewModal;
